/**
 * One BFF user, as a card.
 *
 * The four metrics come straight from the listing (BFF 3.5.0). Each degrades to "—" when the
 * field is absent rather than showing a zero the server never sent.
 *
 * @module Pages/Settings/UserManagement
 */

import { css } from '@emotion/css';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { Role, User } from '../../Types/UserManagement.types';
import {
  formatRoleName,
  formatUserDate,
  getAccountState,
  isFailedAttemptsAlarming,
} from '../../Utils/userPresentation';
import { getInitials } from '@/Shared/components/PageHeader/PageHeader';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { Tag } from '@/Shared/components/Tag/Tag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
`;

const avatarStyle = css`
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: var(--radius-round);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const emailStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const spacerStyle = css`
  flex: 1;
`;

const rolesStyle = css`
  display: flex;
  gap: var(--space-xs);
  flex-wrap: wrap;
`;

const metricsStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-m);
  padding: var(--space-m) 0;
  border-top: 1px solid var(--color-divider);
  border-bottom: 1px solid var(--color-divider);
`;

const metricStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const metricLabelStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const metricValueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  margin-top: auto;
`;

export interface UserCardProps {
  user: User;
  /** Roles held by this user, when they could be resolved. */
  roles?: Role[];
  canManagePermissions: boolean;
  /** Show the deactivate action. Only makes sense for a user who is still active. */
  canDelete: boolean;
  /** Show the reactivate action. Only makes sense for a user who was deactivated. */
  canRestore?: boolean;
  onPermissions: (user: User) => void;
  onView: (user: User) => void;
  onDelete: (user: User) => void;
  onRestore?: (user: User) => void;
}

export const UserCard = ({
  user,
  roles,
  canManagePermissions,
  canDelete,
  canRestore = false,
  onPermissions,
  onView,
  onDelete,
  onRestore,
}: UserCardProps): JSX.Element => {
  const account = getAccountState(user);
  const isDeactivated = user.isActive === false;
  const alarming = isFailedAttemptsAlarming(user.failedLoginAttempts);

  return (
    <article id={`user-card-${user.id}`} className={cardStyle}>
      <div className={headerStyle}>
        <span className={avatarStyle}>{getInitials(user.name)}</span>
        <div className={identityStyle}>
          <span className={nameStyle}>{user.name}</span>
          <span className={emailStyle}>{user.email}</span>
        </div>
        <div className={spacerStyle} />
        {/*
          Deactivated wins over the lock state: a locked account is a temporary security measure,
          a deactivated one is a decision. Showing "Ativa" next to someone who cannot log in at
          all would be the more misleading of the two.
        */}
        <StatusTag status={isDeactivated ? 'neutral' : account.status} size='sm'>
          {isDeactivated ? 'Desativado' : account.label}
        </StatusTag>
      </div>

      {roles && roles.length > 0 ? (
        <div className={rolesStyle}>
          {roles.map(role => (
            <Tag key={role.id} tone='primary' selected>
              {formatRoleName(role.name)}
            </Tag>
          ))}
        </div>
      ) : null}

      <div className={metricsStyle}>
        <div className={metricStyle}>
          <span className={metricLabelStyle}>Permissões</span>
          <span className={metricValueStyle}>{user.permissionCount ?? '—'}</span>
        </div>
        <div className={metricStyle}>
          <span className={metricLabelStyle}>Criado em</span>
          <span className={metricValueStyle}>{formatUserDate(user.createdAt)}</span>
        </div>
        <div className={metricStyle}>
          <span className={metricLabelStyle}>Último acesso</span>
          <span className={metricValueStyle}>{formatUserDate(user.lastLoginAt, 'Nunca')}</span>
        </div>
        <div className={metricStyle}>
          <span className={metricLabelStyle}>Tentativas falhas</span>
          <span
            className={metricValueStyle}
            style={alarming ? { color: 'var(--color-danger)' } : undefined}
          >
            {user.failedLoginAttempts ?? '—'}
          </span>
        </div>
      </div>

      <div className={actionsStyle}>
        {canManagePermissions ? (
          <UTButton
            fitContent
            id={`user-perms-${user.id}`}
            variant='secondary'
            size='small'
            text='Permissões'
            icon='Settings.Gear'
            onClick={() => {
              onPermissions(user);
            }}
          />
        ) : null}

        <div className={spacerStyle} />

        <IconButton
          id={`user-view-${user.id}`}
          icon='Misc.Eye'
          size='sm'
          aria-label={`Ver detalhes de ${user.name}`}
          onClick={() => {
            onView(user);
          }}
        />
        {canDelete ? (
          <IconButton
            id={`user-delete-${user.id}`}
            icon='Misc.Trash'
            size='sm'
            aria-label={`Desativar ${user.name}`}
            onClick={() => {
              onDelete(user);
            }}
          />
        ) : null}
        {canRestore && onRestore ? (
          <UTButton
            fitContent
            id={`user-restore-${user.id}`}
            variant='secondary'
            size='small'
            text='Reativar'
            icon='Arrows.ArrowCounterClockwise'
            onClick={() => {
              onRestore(user);
            }}
          />
        ) : null}
      </div>
    </article>
  );
};
